/**
 * Layout Resources
 *
 * Provides access to layout and form pattern data.
 */

import { McpServer, ResourceTemplate } from '@modelcontextprotocol/sdk/server/mcp.js';
import patternLibrary from '../data/pattern-library.json' with { type: 'json' };
import type { PatternLibrary } from '../types.js';

const patterns = patternLibrary as unknown as PatternLibrary;

/**
 * Register layout and pattern resources
 */
export function registerLayoutResources(server: McpServer): void {
  // All patterns
  server.resource('All Patterns', 'patterns://all', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(patterns, null, 2),
      },
    ],
  }));

  // Layout patterns
  server.resource('Layout Patterns', 'patterns://layouts', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(patterns.layouts, null, 2),
      },
    ],
  }));

  // Form patterns
  server.resource('Form Patterns', 'patterns://forms', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(patterns.forms, null, 2),
      },
    ],
  }));

  // Auth patterns
  server.resource('Auth Patterns', 'patterns://auth', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(patterns.auth || [], null, 2),
      },
    ],
  }));

  // Notification patterns
  server.resource('Notification Patterns', 'patterns://notifications', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(patterns.notifications || [], null, 2),
      },
    ],
  }));

  // Individual pattern by ID
  server.resource(
    'Pattern Details',
    new ResourceTemplate('patterns://{id}', { list: undefined }),
    async (uri, params) => {
      const id = params.id as string;
      const layout = patterns.layouts.find((l) => l.id === id);
      const form = patterns.forms.find((f) => f.id === id);
      const auth = patterns.auth?.find((a) => a.id === id);
      const notification = patterns.notifications?.find((n) => n.id === id);
      const pattern = layout || form || auth || notification;

      if (!pattern) {
        return {
          contents: [
            {
              uri: uri.href,
              mimeType: 'application/json',
              text: JSON.stringify({ error: `Pattern "${id}" not found` }),
            },
          ],
        };
      }

      return {
        contents: [
          {
            uri: uri.href,
            mimeType: 'application/json',
            text: JSON.stringify(pattern, null, 2),
          },
        ],
      };
    }
  );
}
